import _ from 'lodash';
import { sqWorkbenchStore } from '@/core/core.stores';
import { DURATION_TIME_UNITS } from '@/main/app.constants';
import { BASE_TOOL_COMMON_PROPS } from '@/toolSelection/baseTool.constants';
import { BaseToolStore } from '@/toolSelection/baseTool.store';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import type { BaseToolPersistedState } from '@/toolSelection/baseTool.store';
import type { ValueWithUnitsItem } from '@/trend/ValueWithUnits.atom';

interface ShiftConfig {
  offset: number | string;
  duration: number | string;
}

interface QuarterStartConfig {
  month: string;
  day: number | string;
}

interface FormulaResult {
  formula: string;
  parameters: Record<string, unknown>;
}

interface PeriodicConditionState extends BaseToolPersistedState {
  timezone: string | undefined;
  duration: string | undefined;
  frequencies: string[];
  weekStart: string;
  quarterStart: QuarterStartConfig;
  shift: ShiftConfig;
  offset: ValueWithUnitsItem;
  customDuration: ValueWithUnitsItem;
  customPeriod: ValueWithUnitsItem;
  customPeriodDisable: boolean;
}

export type PeriodicConditionDehydratedState = PeriodicConditionState;

export class PeriodicConditionStore extends BaseToolStore {
  type = TREND_TOOLS.PERIODIC_CONDITION;
  static readonly storeName = 'sqPeriodicConditionStore';

  initialize(): void {
    const initialState: PeriodicConditionState = {
      ...BASE_TOOL_COMMON_PROPS,
      timezone: sqWorkbenchStore.userTimeZone?.name,
      duration: undefined,
      frequencies: [],
      weekStart: 'Day.Sunday',
      quarterStart: { month: 'Month.January', day: 1 },
      shift: { offset: 8, duration: 8 },
      offset: { value: 0, units: DURATION_TIME_UNITS[1].unit[0] },
      customDuration: { value: 0, units: DURATION_TIME_UNITS[2].unit[0] },
      customPeriod: { value: 0, units: DURATION_TIME_UNITS[2].unit[0] },
      customPeriodDisable: true,
    };
    this.state = this.immutable(initialState);
  }

  get duration(): string | undefined {
    return this.state.get('duration');
  }

  get timezone(): string | undefined {
    return this.state.get('timezone');
  }

  get frequencies(): string[] {
    return this.state.get('frequencies');
  }

  get shift(): ShiftConfig {
    return this.state.get('shift');
  }

  get weekStart(): string {
    return this.state.get('weekStart');
  }

  get quarterStart(): QuarterStartConfig {
    return this.state.get('quarterStart');
  }

  get offset(): ValueWithUnitsItem | undefined {
    return this.state.get('offset');
  }

  get customDuration(): ValueWithUnitsItem | undefined {
    return this.state.get('customDuration');
  }

  get customPeriod(): ValueWithUnitsItem | undefined {
    return this.state.get('customPeriod');
  }

  get customPeriodDisable(): boolean {
    return this.state.get('customPeriodDisable');
  }

  /**
   * Compute the formula that this tool represents.
   *
   * @returns {Object} Object with properties of "formula" which is the string for the formula and
   *   "parameters" which are all parameters used in the formula (none)
   */
  getFormula(): FormulaResult {
    const duration = this.state.get('duration');
    const frequencies = this.state.get('frequencies') as string[];
    let operatorName = duration;
    let move = '';
    const args: string[] = [];
    const fb = this.formulaBuilder;
    if (duration === 'shifts') {
      const shift = this.state.get('shift') as ShiftConfig;
      args.push(String(shift.offset));
      args.push(String(shift.duration));
    } else if (duration === 'weeks') {
      args.push(String(this.state.get('weekStart')));
    } else if (duration === 'quarters') {
      const quarterStart = this.state.get('quarterStart') as QuarterStartConfig;
      args.push(String(quarterStart.month));
      args.push(String(quarterStart.day));
    } else if (duration === 'periods') {
      const customDuration = this.state.get('customDuration') as ValueWithUnitsItem | undefined;
      if (!_.isNil(customDuration?.value) && customDuration?.units) {
        args.push(fb.duration(customDuration));
      }
      if (!this.state.get('customPeriodDisable')) {
        const customPeriod = this.state.get('customPeriod') as ValueWithUnitsItem | undefined;
        if (!_.isNil(customPeriod?.value) && customPeriod?.units) {
          args.push(fb.duration(customPeriod));
        }
      } else if (!_.isNil(customDuration?.value) && customDuration?.units) {
        args.push(fb.duration(customDuration));
      }
    }

    const tz = this.state.get('timezone');
    const customDuration = this.state.get('customDuration') as ValueWithUnitsItem | undefined;
    if (tz && duration !== 'periods') {
      args.push(`"${tz}"`);
    } else if (tz && duration === 'periods' && ['h', 'day', 'days'].includes(customDuration?.units || '')) {
      args.push('"2000-01-01T00:00:00"');
      args.push(`"${tz}"`);
    }

    if (frequencies.length === 1) {
      // put the specific enum at the first argument
      const frequency = frequencies[0];
      if (!_.isNil(frequency)) {
        args.unshift(String(frequency));
      }
    } else if (frequencies.length > 1) {
      // make a list of conditions, one per frequency
      const mergedArgs = _.map(frequencies, (frequency) =>
        fb.operator(operatorName as string, [String(frequency), ...args]),
      );
      args.length = 0;
      args.push(...mergedArgs);
      // now switch to the operator that combines all these
      operatorName = 'combineWith';
    }

    const offsetDuration = this.state.get('offset') as ValueWithUnitsItem | undefined;
    if (_.isFinite(offsetDuration?.value) && offsetDuration?.value !== 0 && offsetDuration?.units) {
      move = `.${fb.operator('move', [fb.duration(offsetDuration)])}`;
    }

    if (!operatorName) {
      return {
        formula: '',
        parameters: {},
      };
    }

    return {
      formula: fb.operator(operatorName, args) + move,
      parameters: {},
    };
  }

  /**
   * Exports state so it can be used to re-create the state later using `rehydrate`.
   *
   * @return {Object} State for the store
   */
  dehydrate(): PeriodicConditionDehydratedState {
    return this.state.serialize();
  }

  /**
   * Sets the references panel state
   *
   * @param {Object} dehydratedState - Previous state usually obtained from `dehydrate` method.
   */
  rehydrate(dehydratedState: Partial<PeriodicConditionDehydratedState>): void {
    this.state.merge(dehydratedState);
  }

  /** Applies conventions for copying a unit value
   *
   * @param {String} key - The state field being set
   * @param {Object} payload - Object container
   * @param {Number} payload.value - The number that indicates how long the value is
   * @param {String} payload.units - The units that the value represents   */
  setValueUnit(key: string, payload: Partial<ValueWithUnitsItem> | undefined): void {
    if (_.isNil(payload) || _.isNil(payload.value) || _.isNil(payload.units) || _.isNaN(Number(payload.value))) {
      this.state.set(key, {});
    } else {
      this.state.set(key, {
        value: Number(payload.value),
        units: payload.units,
      });
    }
  }

  protected readonly handlers = {
    ...this.baseHandlers,
    /**
     * Set the period of the condition. Resets the frequency choices if the duration changes.
     *
     * @param {Object} payload - Object container
     * @param {String} payload.duration - One of the keys from STANDARD_DURATIONS
     */
    PERIODIC_CONDITION_DURATION: (payload: { duration: string }) => {
      if (this.state.get('duration') !== payload.duration) {
        this.state.set('frequencies', []);
      }
      this.state.set('duration', payload.duration);
    },

    /**
     * Set the timezone of the condition.
     *
     * @param {Object} payload - Object container
     * @param {String} payload.timezone - Name of the timezone
     */
    PERIODIC_CONDITION_TIMEZONE: (payload: { timezone: string }) => {
      this.state.set('timezone', payload.timezone);
    },

    /**
     * Set the day that the week starts on. Only useful for duration of weeks()
     *
     * @param {Object} payload - Object container
     * @param {String} payload.weekStart - The day enum of the week start
     */
    PERIODIC_CONDITION_WEEK_START: (payload: { weekStart: string }) => {
      this.state.set('weekStart', payload.weekStart);
    },

    /**
     * Set the day that the quarter starts on. Only useful for duration of quarters()
     *
     * @param {Object} payload - Object container
     * @param {String} payload.month - The month enum of the quarter start
     * @param {String} payload.day - The number of the first day in the month of the quarter
     */
    PERIODIC_CONDITION_QUARTER_START: (payload: { month: string; day: string }) => {
      this.state.set('quarterStart', {
        month: payload.month,
        day: payload.day,
      });
    },

    /**
     * Set the parameters of the shift operator
     *
     * @param {Object} payload - Object container
     * @param {String} payload.offset - The number of hours after midnight to start
     * @param {String} payload.duration - The number of the hours in the shift. Must be an integer
     */
    PERIODIC_CONDITION_SHIFT: (payload: { offset: string; duration: string }) => {
      this.state.set('shift', {
        offset: payload.offset,
        duration: payload.duration,
      });
    },

    /**
     * Adds or removes the specified frequency from the frequencies list.
     *
     * @param {Object} payload - Object container
     * @param {String} [payload.frequency] - The frequency to toggle. If not provided the list will be cleared.
     */
    PERIODIC_CONDITION_TOGGLE_FREQUENCY: (payload: { frequency?: string }) => {
      const index = _.indexOf(this.state.get('frequencies'), payload.frequency);
      if (!payload.frequency) {
        this.state.set('frequencies', []);
      } else if (index >= 0) {
        this.state.splice('frequencies', [index, 1]);
      } else {
        this.state.push('frequencies', payload.frequency);
      }
    },

    /**
     * Set an offset that can be used to shift the start of the capsules.
     *
     * @param {Object} payload - Object container
     * @param {Number} payload.value - The number that indicates how long the offset is
     * @param {String} payload.units - The units that the value represents
     */
    PERIODIC_CONDITION_OFFSET: (payload: { value: number; units: string }) => {
      this.setValueUnit('offset', payload);
    },
    /**
     * Set the custom period.
     *
     * @param {Object} payload - Object container
     * @param {Number} payload.value - The number that indicates how long the period is
     * @param {String} payload.units - The units that the value represents
     */
    PERIODIC_CONDITION_CUSTOM_PERIOD: (payload: { value: number; units: string }) => {
      this.setValueUnit('customPeriod', payload);
    },
    /**
     * Set the custom duration. If the custom period is disabled, it also keeps the period in lockstep so that
     * it's ready when the user enables the option.
     *
     * @param {Object} payload - Object container
     * @param {Number} payload.value - The number that indicates how long the duration of each capsule is
     * @param {String} payload.units - The units that the value represents
     */
    PERIODIC_CONDITION_CUSTOM_DURATION: (payload: { value: number; units: string }) => {
      if (
        this.state.get('customPeriodDisable') &&
        _.isEqual(this.state.get('customDuration'), this.state.get('customPeriod'))
      ) {
        this.setValueUnit('customPeriod', payload);
      }
      this.setValueUnit('customDuration', payload);
    },
    /**
     * Toggles the custom period
     */
    PERIODIC_CONDITION_CUSTOM_PERIOD_TOGGLE: () => {
      this.state.set('customPeriodDisable', !this.state.get('customPeriodDisable'));
    },
  };
}
